import type { IResource } from "@gettactic/api";
import { useAuthenticated, useI18n } from "@gettactic/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  format,
  isToday,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds
} from "date-fns";
import { toast } from "react-hot-toast";
import { invalidateReservationQueries } from "@/features/reserve/components/Reserve/helpers";
import {
  type SaveReservationData,
  saveReservations
} from "@/features/reserve/utils/utils-reserve";

export const QUICK_RESERVE_START_HOUR = 8;
export const QUICK_RESERVE_DURATION_MINUTES = 540;

export interface QuickReserveVariables {
  resource: IResource;
  date: Date;
}

export interface UseQuickReserveOptions {
  onSuccess?: (variables: QuickReserveVariables) => void;
  onError?: (error: Error) => void;
}

export const useQuickReserve = (options?: UseQuickReserveOptions) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { templates } = useI18n(authenticatedUser);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ resource, date }: QuickReserveVariables) => {
      const userId = authenticatedUser?.user?.id;
      const office = authenticatedUser.offices?.byId?.[resource.office_id];
      if (!userId || !office) {
        throw new Error("Missing required data for quick reserve");
      }

      const time = setMilliseconds(
        setSeconds(
          setMinutes(setHours(new Date(date), QUICK_RESERVE_START_HOUR), 0),
          0
        ),
        0
      );

      const data: SaveReservationData = {
        time,
        duration: { value: QUICK_RESERVE_DURATION_MINUTES },
        resource,
        parking_resource: null,
        users: [{ value: userId }],
        teams: [],
        organizer: { value: "" },
        recurring: "",
        weekDates: [new Date(time)]
      };

      const { hasError, message } = await saveReservations(data, {
        organizerId: null,
        isMeetingRoom: false,
        authUserId: userId,
        office,
        allowOthers: false,
        timeZone: office.time_zone,
        officeId: office.id,
        dateFormat: templates.displayDate,
        orgSlug: authenticatedUser.slug ?? ""
      });

      if (hasError) {
        throw new Error(message || "Failed to quick reserve");
      }
    },
    onSuccess: (_data, variables) => {
      invalidateReservationQueries(queryClient);
      const dayLabel = isToday(variables.date)
        ? "today"
        : format(variables.date, "MMM d");
      toast.success(
        `${variables.resource.name} booked for ${dayLabel}, 8am\u20135pm.`
      );
      options?.onSuccess?.(variables);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to quick reserve");
      options?.onError?.(error);
    }
  });
};
